import { call, put, select, takeLeading } from 'typed-redux-saga'
import {
	NotificationListModel,
	NotificationListNS,
	notificationListApi,
	notificationListSlice,
	notificationListSelector,
	ALL_GROUP_NOTIFICATION,
	NotificationPopupFooterComponent,
	NOTIFICATION_TYPE,
	notificationPopupSlice,
	dictsSelector,
	notifyMessageCreator,
	errorHandler,
	loadersSlice,
	gridSlice,
} from 'Modules'
import { RequestParams, ResponseCatalog } from 'Models'
import { SocketChannel, socketSelector } from 'Modules/Socket'

function* getAll(action: NotificationListNS.GetAll) {
	try {
		const { params, clear = false } = action.payload
		yield* put(gridSlice.actions.setLoading(true))
		const res = yield* call(notificationListApi.getAll, params)
		const notifications = new ResponseCatalog<NotificationListModel>(res)
		if (!clear) {
			const oldNotifications = yield* select(notificationListSelector.getAll)
			notifications.entries = oldNotifications.concat(res.entries)
		}
		yield* put(notificationListSlice.actions.setAll(notifications))
		yield* put(notificationListSlice.actions.setRequestParams(params))
		yield* put(gridSlice.actions.setLoading(false))
	} catch (error) {
		yield* put(gridSlice.actions.setLoading(false))
		yield* call(errorHandler, error)
	}
}

function* deleteOne(action: NotificationListNS.DeleteOne) {
	try {
		yield* call(notificationListApi.deleteOne, action.payload)
		yield* put(notificationListSlice.actions.getStats())
		const requestParams = yield* select(notificationListSelector.getCurrentRequestParams)
		const res = yield* call(notificationListApi.getAll, requestParams)
		if (res?.total_entries === 0)
			yield* put(notificationListSlice.actions.changeGroup(ALL_GROUP_NOTIFICATION))
		else {
			const notifications = new ResponseCatalog<NotificationListModel>(res)
			yield* put(notificationListSlice.actions.setAll(notifications))
		}
		yield* call(notifyMessageCreator, 'common.message_delete.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* readOne(action: NotificationListNS.ReadOne) {
	try {
		yield* call(notificationListApi.readOne, action.payload)
		yield* put(notificationListSlice.actions.getStats())
		const requestParams = yield* select(notificationListSelector.getCurrentRequestParams)
		yield* put(notificationListSlice.actions.getAll({ params: requestParams, clear: true }))
		yield* call(notifyMessageCreator, 'common.message_read.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* readAll(action: NotificationListNS.ReadAll) {
	try {
		const group = action.payload
		yield* call(notificationListApi.readAll, group === ALL_GROUP_NOTIFICATION ? '' : group)
		yield* put(notificationListSlice.actions.getStats())
		const requestParams = yield* select(notificationListSelector.getCurrentRequestParams)
		yield* put(notificationListSlice.actions.getAll({ params: requestParams, clear: true }))
		yield* call(notifyMessageCreator, 'common.all_messages_read.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* deleteAll(action: NotificationListNS.DeleteAll) {
	try {
		const group = action.payload
		yield* call(notificationListApi.deleteAll, group === ALL_GROUP_NOTIFICATION ? '' : group)
		yield* put(notificationListSlice.actions.changeGroup(ALL_GROUP_NOTIFICATION))
		yield* call(notifyMessageCreator, 'common.all_messages_delete.success')
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getStats() {
	try {
		const res = yield* call(notificationListApi.getStats)
		yield* put(notificationListSlice.actions.setStats(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* clickViewMessage(action: NotificationListNS.ClickViewMessage) {
	try {
		yield* put(notificationListSlice.actions.setVisibleDrawer(true))
		yield* put(notificationListSlice.actions.changeGroup(action.payload))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

export function* getMessage(action: NotificationListNS.GetMessage) {
	try {
		yield* put(notificationListSlice.actions.setMessage(action.payload))
		yield* put(notificationListSlice.actions.getStats())
		const currentGroup = yield* select(notificationListSelector.getGroup)
		if (currentGroup === ALL_GROUP_NOTIFICATION || currentGroup === action.payload?.group) {
			const requestParams = yield* select(notificationListSelector.getCurrentRequestParams)
			yield* put(notificationListSlice.actions.getAll({ params: requestParams, clear: true }))
		}
		const groupsTranslate = yield* select(dictsSelector.getNotificationTemplateGroups)
		const channels = yield* select(socketSelector.selectChannels)
		const notify = {
			type: action.payload.type as unknown as NOTIFICATION_TYPE,
			title: groupsTranslate?.find((group): boolean => action.payload.group === group.id)?.name,
			close: true,
			body: action.payload.subject,
			groupMenu: action.payload.group,
			footer: action.payload.action
				? NotificationPopupFooterComponent({
						title: action.payload?.btnTitle ?? 'Перейти',
						action: action.payload.action,
				  })
				: null,
		}
		if (!([...channels].includes(SocketChannel.LEAD_CHANNEL) && action.payload.group === 'lead'))
			yield* put(notificationPopupSlice.actions.add(notify))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* changeGroup(action: NotificationListNS.ChangeGroup) {
	try {
		const group = action.payload
		yield* put(notificationListSlice.actions.setGroup(group))
		yield* put(notificationListSlice.actions.getStats())
		const newParams = new RequestParams(
			1,
			30,
			null,
			group === ALL_GROUP_NOTIFICATION ? {} : { group }
		)
		yield* put(notificationListSlice.actions.getAll({ params: newParams, clear: true }))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getAllBik(action: NotificationListNS.GetAllBik) {
	try {
		const { query, clear = false } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(notificationListApi.getBankApi, query)
		let bik = res
		if (!clear) {
			const oldNotifications = yield* select(notificationListSelector.getBik)
			bik = oldNotifications.concat(res)
		}
		yield* put(notificationListSlice.actions.setAllBik(bik))
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* notificationListSaga() {
	yield* takeLeading(notificationListSlice.actions.getAll.type, getAll)
	yield* takeLeading(notificationListSlice.actions.getStats, getStats)
	yield* takeLeading(notificationListSlice.actions.changeGroup, changeGroup)
	yield* takeLeading(notificationListSlice.actions.deleteOne.type, deleteOne)
	yield* takeLeading(notificationListSlice.actions.readOne.type, readOne)
	yield* takeLeading(notificationListSlice.actions.readAll.type, readAll)
	yield* takeLeading(notificationListSlice.actions.deleteAll.type, deleteAll)
	yield* takeLeading(notificationListSlice.actions.clickViewMessage.type, clickViewMessage)
	yield* takeLeading(notificationListSlice.actions.getAllBik.type, getAllBik)
	yield* takeLeading(notificationListSlice.actions.getMessage.type, getMessage)
}
